import type { SxProps } from './sxProps';

const SYSTEM_PROPS = [
  'gap',
  'rowGap',
  'columnGap',
  'width',
  'height',
  'minWidth',
  'maxWidth',
  'minHeight',
  'maxHeight',
  'display',
  'flexDirection',
  'flexWrap',
  'alignItems',
  'alignContent',
  'justifyContent',
  'flex',
  'flexGrow',
  'flexShrink',
  'gridTemplateColumns',
  'gridTemplateRows',
  'position',
  'top',
  'right',
  'bottom',
  'left',
  'zIndex',
  'overflow',
  'boxSizing',
  'fontWeight',
  'fontSize',
  'textAlign',
  'lineHeight',
  'borderRadius',
  'padding',
  'margin',
  'objectFit',
] as const;

type SystemProp = (typeof SYSTEM_PROPS)[number];

const SYSTEM_PROP_SET = new Set<string>(SYSTEM_PROPS);

function isSystemProp(key: string): key is SystemProp {
  return SYSTEM_PROP_SET.has(key);
}

const NON_DOM = new Set([
  'sx',
  'component',
  'variant',
  'color',
  'size',
  'spacing',
  'direction',
  'align',
  'fullWidth',
  'startIcon',
  'endIcon',
  'disableRipple',
  'disableFocusRipple',
  'disableElevation',
  'disablePadding',
  'slotProps',
  'anchor',
  'anchorEl',
  'open',
  'onClose',
  'anchorOrigin',
  'transformOrigin',
  'primary',
  'secondary',
  'expandIcon',
  'inputRef',
  'startAdornment',
  'endAdornment',
  'label',
  'labelId',
  'multiline',
  'minRows',
  'maxRows',
  'InputProps',
  'inputProps',
  'InputLabelProps',
  'noWrap',
  'gutterBottom',
  'paragraph',
  'elevation',
  'square',
  'disableGutters',
  'fixed',
  'exclusive',
  'arrow',
  'placement',
  'options',
  'groupBy',
  'getOptionLabel',
  'renderOption',
  'renderGroup',
  'renderInput',
  'filterOptions',
  'freeSolo',
  'select',
  'SelectProps',
]);

type EventHandler = (event: any, ...args: any[]) => void;

export type LooseProps = Omit<
  React.HTMLAttributes<HTMLElement>,
  | 'onClick'
  | 'onChange'
  | 'onBlur'
  | 'onFocus'
  | 'onKeyDown'
  | 'onKeyUp'
  | 'onSubmit'
  | 'onMouseDown'
  | 'color'
> & {
  sx?: SxProps;
  component?: React.ElementType;
  href?: string;
  type?: string;
  name?: string;
  value?: any;
  disabled?: boolean;
  checked?: boolean;
  placeholder?: string;
  required?: boolean;
  autoComplete?: string;
  target?: string;
  rel?: string;
  htmlFor?: string;
  variant?: any;
  color?: any;
  size?: any;
  spacing?: number | string;
  direction?: SxProps['flexDirection'];
  align?: SxProps['textAlign'];
  alignItems?: SxProps['alignItems'];
  justifyContent?: SxProps['justifyContent'];
  fullWidth?: any;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  fontWeight?: SxProps['fontWeight'];
  textAlign?: SxProps['textAlign'];
  label?: React.ReactNode;
  multiline?: boolean;
  minRows?: number;
  maxRows?: number;
  rows?: number;
  slotProps?: {
    paper?: { sx?: SxProps };
    transition?: { onEntered?: () => void };
  };
  anchor?: any;
  open?: boolean;
  onClose?: (...args: any[]) => void;
  anchorEl?: any;
  anchorOrigin?: any;
  transformOrigin?: any;
  disablePadding?: boolean;
  primary?: React.ReactNode;
  secondary?: React.ReactNode;
  expandIcon?: React.ReactNode;
  inputRef?: any;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  maxWidth?: SxProps['maxWidth'];
  width?: SxProps['width'];
  height?: SxProps['height'];
  display?: SxProps['display'];
  gap?: SxProps['gap'];
  flex?: SxProps['flex'];
  gridTemplateColumns?: SxProps['gridTemplateColumns'];
  exclusive?: boolean;
  arrow?: boolean;
  placement?: any;
  underline?: any;
  noWrap?: boolean;
  gutterBottom?: boolean;
  paragraph?: boolean;
  disableRipple?: boolean;
  disableFocusRipple?: boolean;
  disableElevation?: boolean;
  elevation?: any;
  square?: boolean;
  fixed?: boolean;
  disableGutters?: boolean;
  InputProps?: any;
  inputProps?: any;
  InputLabelProps?: any;
  labelId?: string;
  groupBy?: any;
  getOptionLabel?: any;
  renderOption?: any;
  renderGroup?: any;
  renderInput?: any;
  filterOptions?: any;
  options?: any;
  freeSolo?: boolean;
  select?: boolean;
  selected?: boolean;
  flexItem?: boolean;
  padding?: SxProps['padding'];
  margin?: SxProps['margin'];
  borderRadius?: SxProps['borderRadius'];
  expanded?: boolean;
  src?: string;
  alt?: string;
  autoPlay?: boolean;
  loop?: boolean;
  playsInline?: boolean;
  controls?: boolean;
  muted?: boolean;
  preload?: string;
  onEnded?: (event: any) => void;
  objectFit?: SxProps['objectFit'];
  onClick?: EventHandler;
  onChange?: EventHandler;
  onBlur?: EventHandler;
  onFocus?: EventHandler;
  onKeyDown?: EventHandler;
  onKeyUp?: EventHandler;
  onSubmit?: EventHandler;
  onMouseDown?: EventHandler;
};

export function splitProps(props: Record<string, any>, extraOmit: string[] = []) {
  const omit = new Set(extraOmit);
  const system: Record<string, unknown> = {};
  const dom: Record<string, any> = {};

  Object.entries(props).forEach(([key, value]) => {
    if (value === undefined) return;
    if (isSystemProp(key)) {
      system[key] = value;
      return;
    }
    if (NON_DOM.has(key) || omit.has(key)) return;
    dom[key] = value;
  });

  return { system: system as Pick<SxProps, SystemProp>, dom };
}
